import React from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Divider, Grid, useMediaQuery, useTheme,
} from '@material-ui/core';
import clsx from 'clsx';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import Spacer from '@/components/ui-components/Spacer';
import useMoneyFormat from '@/hooks/useMoneyFormat';
import Status from './Status';
import Typography from '../../components/Typography';
import useKFormatter from '@/hooks/useKFormatter';

interface SVGIconProps {
  eventType:
  | 'A'
  | 'D'
  | 'C'
  | 'UPDATE'
  | 'ACHIEVEMENT'
  | 'COMPANY_EVENT'
  | 'LAUNCH'
  | 'FOUNDED';
}

export const SVGIcon = ({ eventType }: SVGIconProps) => {
  const theme = useTheme();
  switch (eventType) {
    case 'UPDATE':
      return <Typography variant="secondary2bold">📩</Typography>;
    case 'ACHIEVEMENT':
      return <Typography variant="secondary2bold">🏅</Typography>;
    case 'LAUNCH':
      return <Typography variant="secondary2bold">🚀</Typography>;
    case 'FOUNDED':
      return <Typography variant="subheader2">🏛</Typography>;
    case 'A':
      return (
        <Typography
          variant="secondary2bold"
          style={{ color: theme.palette.greyScale.extralight }}
        >
          A+
        </Typography>
      );
    case 'C':
      return (
        <Typography
          variant="secondary2bold"
          style={{ color: theme.palette.greyScale.extralight }}
        >
          CF
        </Typography>
      );
    case 'D':
      return (
        <Typography
          variant="secondary2bold"
          style={{ color: theme.palette.greyScale.extralight }}
        >
          D
        </Typography>
      );
    default:
      return (
        <Typography
          variant="secondary2bold"
          style={{ color: theme.palette.greyScale.extralight }}
        >
          A
        </Typography>
      );
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  horizontalTimelineIcon: {
    minWidth: 48,
    minHeight: 48,
    borderRadius: '50%',
    backgroundColor: theme.palette.greyScale.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateTextLeft: {
    marginTop: theme.spacing(1.5),
    width: 90,
    color: theme.palette.greyScale.dark,
  },
  itemContainer: {
    minHeight: 80,
    position: 'relative',
    paddingLeft: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      maxWidth: 'calc(100% - 25px)',
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: 'calc(100% - 150px)',
    },
  },
  verticalBar: {
    marginLeft: 22,
    backgroundColor: theme.palette.greyScale.main,
    height: '100%',
    width: 2,
  },
  mobileVerticalBar: {
    marginLeft: 22,
    backgroundColor: theme.palette.greyScale.main,
    height: '100%',
    width: 2,
  },
  currentRoundColorModifier: {
    backgroundColor: theme.palette.primary.main,
  },
  offeringSuccessText: {
    color: theme.palette.success.main,
  },
  offeringFailureText: {
    color: theme.palette.error.main,
  },
  platformLogo: {
    marginLeft: theme.spacing(1),
  },
  verticalDivider: {
    '&::after': {
      content: "''",
      width: 0,
      height: '90%',
      position: 'absolute',
      border: `1px solid ${theme.palette.greyScale.light}`,
      top: '5%',
      right: '50%',
    },
  },
  horizontalDivider: {
    borderBottom: `1px solid ${theme.palette.greyScale.light}`,
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  websiteLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
  },
  link: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
}));

interface ItemType {
  id: string;
  //   eventType: 'A' | 'D' | 'C' | 'UPDATE' | 'ACHIEVEMENT' | 'COMPANY_EVENT' | 'LAUNCH' | 'FOUNDED'
}

interface UpdateType extends ItemType {
  eventType: 'UPDATE';
  headline: string;
  body: string[];
  photos: {
    url: string;
    caption: string;
  }[];
}
interface LaunchType extends ItemType {
  eventType: 'LAUNCH';
  date: string;
}
interface RegType extends ItemType {
  eventType: 'A' | 'C' | 'D';
  date: string;
  status:
  | 'TESTING_THE_WATERS'
  | 'LIVE'
  | 'EXPIRED_FUNDED'
  | 'EXPIRED_FAIL'
  | 'WITHDRAWN';
  platform?: {
    logo: string;
    name: string;
  };
  slug: string;
  minGoal?: number;
  maxGoal?: number;
  raised?: number;
  preMoneyValuation?: number;
  investorsCount?: number;
  endDate?: string;
  urlOffer?: string;
  urlEdgar?: string;
}

interface ArchivType extends ItemType {
  eventType: 'ACHIEVEMENT';
  headline: string;
}

interface FoundType extends ItemType {
  eventType: 'FOUNDED';
  date: string;
}

type AllItemType = UpdateType | LaunchType | RegType | ArchivType | FoundType;

export type EventType = {
  timestamp: number;
  isCurrentRound: Boolean;
  items: AllItemType[];
};

export interface ElRefsType {
  [id: string]: React.RefObject<HTMLDivElement>;
}

interface Props {
  events: EventType[];
  elRefs: ElRefsType;
}

const defaultProps = {};

const AchievementType = ({ item }) => {
  const { t } = useTranslation('markets');

  switch (item.type) {
    case 'MINIMUM':
      return (
        <Typography variant="title2">
          {t('markets.detailView.timeline.achievementMin', { raised: useKFormatter(item.minGoal), investors: item.investors })}
        </Typography>
      );
    case 'MAXIMUM':
      return (
        <Typography variant="title2">
          {t('markets.detailView.timeline.achievementMax', { raised: useKFormatter(item.maxGoal), investors: item.investors })}
        </Typography>
      );
    case 'CLOSED_SUCESSFULLY':
      return (
        <Typography variant="title2">
          {t('markets.detailView.timeline.achievementClosedSuccess', { raised: useKFormatter(item.raised), investors: item.investors })}

        </Typography>
      );
    case 'CLOSED_UNSUCCESSFULLY':
      return (
        <Typography variant="title2">
          {t('markets.detailView.timeline.achievementClosedUnsuccess', { raised: useKFormatter(item.raised), investors: item.investors, minimum: useKFormatter(item.minGoal) })}
        </Typography>
      );
    case 'WITHDRAWAL':
      return (
        <Typography variant="title2">
          {t('markets.detailView.timeline.achievementWithdrawal')}
        </Typography>
      );
    default: return (
      <Typography variant="title2">
        {t('markets.detailView.timeline.achievement')}
      </Typography>
    );
  }
};

const ItemContent = ({
  item,
  numberOfItems,
}: {
  item: AllItemType;
  numberOfItems: number;
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation('markets');
  switch (item.eventType) {
    case 'LAUNCH':
      return (
        <Typography variant="title2">
          {t('markets.detailView.timeline.launched')}
          {new Date(item.date).toLocaleDateString('en', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </Typography>
      );
    case 'UPDATE':
      return (
        <div>
          <Typography variant="title2">{item.headline}</Typography>
          <Spacer spacing={1} />
          {item.body.map((bItem) => (
            <Typography variant="base2" paragraph key={bItem}>
              {bItem}
            </Typography>
          ))}
        </div>
      );
    case 'ACHIEVEMENT':
      // return <Typography variant="title2">{item.minGoal}</Typography>;
      return <AchievementType item={item} />;
    case 'FOUNDED':
      return (
        <Typography variant="title2">
          {`Founded on ${new Date(item.date).toLocaleDateString('en', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}`}
        </Typography>
      );
    case 'A':
    case 'C':
    case 'D':
      return (
        <>
          <div className={classes.header}>
            <Typography variant="title2">{item.eventType}</Typography>
            {item.status === 'LIVE' ? <Status /> : <Status isClosed />}
          </div>
          <div>
            {item.status === 'EXPIRED_FUNDED' && (
              <>
                <Typography
                  variant="secondary2"
                  className={classes.offeringSuccessText}
                >
                  {t('markets.detailView.timeline.successfullyClosed')}
                </Typography>
                <Spacer spacing={2} />
              </>
            )}
            {item.status === 'EXPIRED_FAIL' && (
              <>
                <Typography
                  variant="secondary2"
                  className={classes.offeringFailureText}
                >
                  {t('markets.detailView.timeline.notReachedMinimum')}
                </Typography>
                <Spacer spacing={2} />
              </>
            )}

            <Grid
              container
              spacing={1}
              direction="row"
              justify="space-between"
              alignItems="stretch"
            >
              {/* multi-column if number of items === 1 */}
              <Grid
                item
                xs={12}
                sm={numberOfItems > 1 ? 12 : 6}
                md={numberOfItems > 1 ? 12 : 5}
              >
                {item.platform && (
                  <>
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                    >
                      <Typography variant="base2">
                        {' '}
                        {t('markets.detailView.timeline.platform')}
                      </Typography>
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        <Typography variant="subheader">
                          {item.platform.name}
                        </Typography>
                        {item.platform.logo && (
                        <Image
                          src={item.platform.logo}
                          alt={item.platform.name}
                          width={24}
                          height={24}
                          className={classes.platformLogo}
                        />
                        )}
                      </Box>
                    </Box>
                    <Spacer spacing={1} />
                  </>
                )}
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Typography variant="base2">
                    {' '}
                    {t('markets.detailView.timeline.minimumGoal')}
                  </Typography>
                  <Typography variant="subheader">
                    {useMoneyFormat(item.minGoal, i18n.language, 'USD', 'currency', 'symbol',
                      0,
                      2)}
                  </Typography>
                </Box>
                <Spacer spacing={1} />

                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Typography variant="base2">
                    {' '}
                    {t('markets.detailView.timeline.maximumGoal')}
                  </Typography>
                  <Typography variant="subheader">
                    {useMoneyFormat(item.maxGoal, i18n.language, 'USD', 'currency', 'symbol',
                      0,
                      2)}
                  </Typography>
                </Box>
                <Spacer spacing={1} />

                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Typography variant="base2">
                    {' '}
                    {t('markets.detailView.timeline.raised')}
                  </Typography>
                  <Typography variant="subheader">
                    {useMoneyFormat(item.raised, i18n.language, 'USD', 'currency', 'symbol',
                      0,
                      2)}
                  </Typography>
                </Box>
                <Spacer spacing={1} />

                {numberOfItems > 1 && (
                  <>
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                    >
                      <Typography variant="base2">
                        {' '}
                        {t('markets.detailView.timeline.investors')}
                      </Typography>
                      <Typography variant="subheader">
                        {item.investorsCount}
                      </Typography>
                    </Box>
                    <Spacer spacing={1} />

                    {item.endDate && (
                      <>
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                        >
                          <Typography variant="base2">
                            {t('markets.detailView.timeline.endDate')}
                          </Typography>
                          <Typography variant="subheader">
                            {new Date(item.endDate).toLocaleDateString('en', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                        </Box>
                        <Spacer spacing={1} />
                      </>
                    )}
                    {item.preMoneyValuation && (
                      <>
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                        >
                          <Typography variant="base2">
                            {t('markets.detailView.timeline.premoney')}
                          </Typography>
                          <Typography variant="subheader">
                            {useMoneyFormat(item.preMoneyValuation, i18n.language, 'USD', 'currency', 'symbol',
                              0,
                              2)}
                          </Typography>
                        </Box>
                        <Spacer spacing={1} />
                      </>
                    )}
                  </>
                )}
              </Grid>
              {numberOfItems < 2 && (
                <Grid item xs={12} sm={6} md={5}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                  >
                    <Typography variant="base2">
                      {t('markets.detailView.timeline.investors')}
                    </Typography>
                    <Typography variant="subheader">
                      {item.investorsCount}
                    </Typography>
                  </Box>
                  <Spacer spacing={1} />

                  {item.endDate && (
                    <>
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                      >
                        <Typography variant="base2">
                          {' '}
                          {t('markets.detailView.timeline.endDate')}
                        </Typography>
                        <Typography variant="subheader">
                          {new Date(item.endDate).toLocaleDateString('en', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </Typography>
                      </Box>
                      <Spacer spacing={1} />
                    </>
                  )}
                  {item.preMoneyValuation && (
                    <>
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                      >
                        <Typography variant="base2">
                          {t('markets.detailView.timeline.premoney')}
                        </Typography>
                        <Typography variant="subheader">
                          {useMoneyFormat(item.preMoneyValuation, i18n.language, 'USD', 'currency', 'symbol',
                            0,
                            2)}
                        </Typography>
                      </Box>
                      <Spacer spacing={1} />
                    </>
                  )}
                </Grid>
              )}
            </Grid>
            <Spacer spacing={3} />
            <div className={classes.link}>
              <a
                href={item.urlOffer ? item.urlOffer : item.urlEdgar}
                className={classes.websiteLink}
                target="_blank"
                rel="noreferrer"
              >
                <Typography variant="secondary2bold" color="primary">
                  {t('markets.detailView.timeline.goToOffer')}
                </Typography>
              </a>
            </div>
            <Spacer spacing={2} />
          </div>
        </>
      );

    default:
      return null;
  }
};

const VerticalTimeline = ({ events, elRefs }: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  return (
    <>
      {events.map((event, index) => {
        const isLastElement = index === events.length - 1;
        return (
          <div
            key={event.timestamp}
            ref={elRefs[`event-${event.timestamp}`]}
            className={classes.root}
          >
            {/* if desktop && show date on the left */}
            {!isMobile && (
              <Typography
                variant="small2medium"
                className={classes.dateTextLeft}
              >
                {new Date(event.timestamp).toLocaleDateString('en', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Typography>
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {event.items.map((item) => (
                <>
                  <div
                    key={item.id}
                    className={clsx(
                      classes.horizontalTimelineIcon,
                      event.isCurrentRound && classes.currentRoundColorModifier,
                    )}
                  >
                    <Typography variant="subheader2">
                      <SVGIcon eventType={item.eventType} />
                    </Typography>
                  </div>
                  {isMobile && !isLastElement && (
                    <div
                      className={clsx(
                        classes.mobileVerticalBar,
                        event.isCurrentRound
                          && classes.currentRoundColorModifier,
                      )}
                    />
                  )}
                </>
              ))}

              {!isLastElement && (
                <div
                  className={clsx(
                    classes.verticalBar,
                    event.isCurrentRound && classes.currentRoundColorModifier,
                  )}
                />
              )}
            </div>

            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="stretch"
              className={clsx(
                classes.itemContainer,
                !isLastElement && classes.horizontalDivider,
              )}
              spacing={1}
            >
              {isMobile && (
                <Typography
                  variant="small2medium"
                  style={{ color: theme.palette.greyScale.dark }}
                >
                  {new Date(event.timestamp).toLocaleDateString('en', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </Typography>
              )}
              {event.items.map((item, j) => (
                <Grid
                  item
                  xs={12}
                  sm={event.items.length > 1 ? 5 : 12}
                  key={item.id}
                  className={clsx(
                    event.items.length > 1 && j % 2 === 0 && !isMobile
                      ? classes.verticalDivider
                      : undefined,
                    isMobile && !isLastElement && classes.horizontalDivider,
                  )}
                >
                  <ItemContent item={item} numberOfItems={event.items.length} />
                </Grid>
              ))}
            </Grid>
            {!isLastElement && (
              <>
                <Divider />
                <Spacer spacing={2} />
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

VerticalTimeline.defaultProps = (defaultProps as unknown) as Partial<Props>;

export default VerticalTimeline;
